import { calculateTotal } from '@calculations/price/utilities/total';
import { SnapRoundingOptionsCustom } from '@calculations/round/decimal';
import { PayloadAction } from '@reduxjs/toolkit';
import { SyncSnapQuoteLineOutput } from '@snap-types/actions/sync-snap-quote-line';
import { WritableDraft } from 'immer';

import { QuoteLineParams } from '../../types/shared';
import { QuoteBuilderStateType } from '../../types/state';
import { applyQuotePriceRecalcResult } from '../calculations/utilities/apply-quote-price-recalc-result';
import { getQuoteLineOrThrow } from '../utilities/action-guards';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';
import { transformSnapWorkOrderToStoreState } from '../utilities/transform-work-order';
import { withSaveStateUpdate } from '../utilities/with-save-state-update';

export function quoteLineSyncTriggered(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<QuoteLineParams>,
) {
  const { quoteLineUuid } = action.payload;

  const quoteLine = getQuoteLineOrThrow(state, quoteLineUuid);

  quoteLine.status = 'syncing';
}

/**
 * A sync produces two things: the work order Visual holds for the part, and the
 * costs and price recalculated from it. Everything else in the response is just
 * what the line already held server-side, so only those two are merged back.
 *
 * Assigning the whole response would reset any field edited since the last save
 * — the endpoint never persists the line's editable fields, and it drops the
 * per-break lead time from the request body, so the response carries the last
 * saved values for both and would overwrite the newer ones in the store.
 */
function quoteLineSyncSucceededBase(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<
    QuoteLineParams & { updatedQuoteLine: SyncSnapQuoteLineOutput }
  >,
) {
  const { quoteLineUuid, updatedQuoteLine } = action.payload;

  const quoteLine = getQuoteLineOrThrow(state, quoteLineUuid);

  deleteIssue(
    state,
    buildIssueId({
      area: 'quoteLine',
      quoteLineUuid,
      type: 'rejected',
    }),
  );

  quoteLine.snapWorkOrder = transformSnapWorkOrderToStoreState(
    updatedQuoteLine.snapWorkOrder,
  );

  for (const updatedQuotePrice of updatedQuoteLine.snapQuotePrices) {
    const quotePrice = quoteLine.quotePrices[updatedQuotePrice.uuid];

    // A break deleted while the sync was in flight has nothing to reprice
    if (!quotePrice) {
      continue;
    }

    const unitPrice = updatedQuotePrice.unitPrice;

    applyQuotePriceRecalcResult(state, {
      quoteLineUuid,
      quotePriceUuid: updatedQuotePrice.uuid,
      quotePrice,
      patch: {
        unitCost: updatedQuotePrice.unitCost,
        unitMaterialCost: updatedQuotePrice.unitMaterialCost,
        unitLaborCost: updatedQuotePrice.unitLaborCost,
        unitBurdenCost: updatedQuotePrice.unitBurdenCost,
        unitServiceCost: updatedQuotePrice.unitServiceCost,
        unitVariableCosts: updatedQuotePrice.unitVariableCosts,
        requiresUnsupportedCalculations:
          updatedQuotePrice.requiresUnsupportedCalculations,
        unitPrice,
        // Syncing reprices from the new cost, so a manual price or margin no
        // longer applies
        userUnitPrice: null,
        grossMargin: updatedQuotePrice.grossMargin,
        userGrossMargin: null,
        totalPrice: calculateTotal(
          quotePrice.quantity,
          unitPrice,
          SnapRoundingOptionsCustom(state),
        ),
      },
      rawFieldsToClear: ['unitCost', 'unitPrice', 'grossMargin'],
    });
  }

  quoteLine.status = 'ready';
}

export const quoteLineSyncSucceeded = withSaveStateUpdate(
  quoteLineSyncSucceededBase,
);

export function quoteLineSyncFailed(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<QuoteLineParams>,
) {
  const { quoteLineUuid } = action.payload;

  const quoteLine = getQuoteLineOrThrow(state, quoteLineUuid);

  quoteLine.status = 'ready';

  upsertIssue(
    state,
    buildIssueId({
      area: 'quoteLine',
      quoteLineUuid,
      type: 'rejected',
    }),
    {
      title: 'Failed to Sync Quote Line',
      description: 'Quote line could not be synced with Visual',
      area: 'quoteLine',
      quoteLineUuid,
      severity: 'error',
      type: 'rejected',
      action: {
        type: 'unrecoverable',
      },
    },
  );
}
